/**
 * /admin — instance-owner-only system admin (Jared). Usage, Backups,
 * Playlists, Videos, an email diagnostic, and Circle administration
 * (surface toggles for circles, which only the owner creates). Per-
 * household management (rename / members / invite) lives at /household
 * for every family lead; this page is app-wide ops, owner only.
 *
 * Non-owners get redirected to /today; the entry point in Settings is
 * also conditionally rendered, so this is a hard gate for the chrome
 * and a soft gate for the API (which keeps its own 403 on every
 * endpoint touched here).
 */
import { useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { ScreenHeader } from '../components/ScreenHeader';
import { Section } from '../components/Section';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { Button } from '../components/Button';
import { useMyCircles, type Circle } from '../lib/circles';
import { TAG_LABEL, VIDEO_TAGS, type VideoRow, type VideoTag } from '../lib/video-tags';

interface UsageSnapshot {
  today: {
    date: string;
    worker: number;
    d1_read: number;
    d1_write: number;
    r2_op: number;
  };
  percents: { worker: number; d1_read: number; d1_write: number; r2_op: number };
  ceilings: {
    worker_per_day: number;
    d1_reads_per_day: number;
    d1_writes_per_day: number;
    r2_class_a_per_month: number;
    r2_storage_gb: number;
  };
  photos: { count: number };
  contentRunwayDays: number;
}

interface BackupRun {
  id: string;
  startedAt: number;
  finishedAt: number | null;
  kind: 'd1' | 'r2_mirror' | 'manual';
  status: 'running' | 'success' | 'failed';
  bytes: number | null;
  error: string | null;
  destination: string;
}

interface PlaylistFeed {
  id: string;
  playlistId: string;
  url: string;
  name: string;
  enabled: boolean;
  defaultTags: string;
  createdAt: number;
}

export default function AdminPage() {
  const auth = useAuth();
  if (auth.state !== 'signed-in') return null;
  // Hard gate: non-owners go back to Today. The API also returns 403
  // on every section's endpoint, so this is belt + suspenders.
  if (!auth.me.isOwner) return <Navigate to="/today" replace />;

  return (
    <>
      <ScreenHeader
        title="Admin"
        subtitle="Circles, content, and system."
        right={
          <Link to="/you" className="text-soft text-sm">
            ← Settings
          </Link>
        }
      />
      <div className="px-5 pt-4 pb-6">
        <CirclesAdminSection />
        <EmailDiagnosticSection />
        <UsageSection />
        <BackupsSection />
        <PlaylistFeedsSection />
        <VideosCurationSection />
      </div>
    </>
  );
}

// ------------------------------------------------------------ circles admin

function CirclesAdminSection() {
  const qc = useQueryClient();
  const circles = useMyCircles();
  const update = useMutation({
    mutationFn: ({ id, surfaces }: { id: string; surfaces: Circle['surfaces'] }) =>
      api.patch(`/circles/${id}`, { surfaces }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['circles'] }),
  });

  if (!circles.data) return null;
  const admined = circles.data.circles.filter((c) => c.role === 'admin');

  return (
    <Section title="Circles">
      <p className="text-soft text-sm mb-3">
        Circles you administer. Toggle which surfaces each circle can see — gating is
        enforced server-side per-circle, even if a member tries to share into a disabled
        surface. Use{' '}
        <Link to="/generations/manage" className="underline text-oxblood">
          Manage circles
        </Link>{' '}
        for the full create/join/leave flow.
      </p>
      {admined.length === 0 ? (
        <p className="text-soft text-sm italic">
          You don't administer any circles. Anyone you create becomes one yours; circles
          you join start as a member.
        </p>
      ) : (
        <ul className="space-y-3">
          {admined.map((c) => (
            <li key={c.id} className="py-2 border-b border-line last:border-b-0">
              <div className="flex items-baseline justify-between">
                <h3 className="font-serif text-lg text-ink">{c.name}</h3>
              </div>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {(['photos', 'prayer', 'chat'] as const).map((surface) => {
                  const on = c.surfaces[surface];
                  return (
                    <button
                      key={surface}
                      onClick={() =>
                        update.mutate({
                          id: c.id,
                          surfaces: { ...c.surfaces, [surface]: !on },
                        })
                      }
                      className={`px-2.5 h-7 rounded-full text-xs border ${
                        on
                          ? 'bg-oxblood text-paper border-oxblood'
                          : 'text-soft border-line bg-paper'
                      }`}
                    >
                      {surface[0]!.toUpperCase() + surface.slice(1)}
                    </button>
                  );
                })}
              </div>
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

// ------------------------------------------------------------------ usage

function UsageSection() {
  const usage = useQuery({
    queryKey: ['usage'],
    queryFn: () => api.get<UsageSnapshot>('/usage'),
    retry: false,
    refetchOnWindowFocus: true,
  });
  if (usage.error instanceof ApiError && usage.error.status === 403) return null;
  if (!usage.data) {
    if (usage.isLoading) return null;
    return (
      <Section title="Usage">
        <p className="text-soft text-sm italic">Couldn't load usage.</p>
      </Section>
    );
  }
  return (
    <Section title="Usage">
      <p className="text-soft text-sm mb-3">
        Cloudflare free-tier headroom for today. We sample, so values are approximate —
        but trustworthy enough to warn before anything overruns.
      </p>
      <Meter label="Worker requests" pct={usage.data.percents.worker} />
      <Meter label="D1 reads" pct={usage.data.percents.d1_read} />
      <Meter label="D1 writes" pct={usage.data.percents.d1_write} />
      <Meter label="R2 ops" pct={usage.data.percents.r2_op} />
      <Row label="Photos stored">
        <span className="text-soft">{usage.data.photos.count}</span>
      </Row>
      <Row label="Verse content runway">
        <span
          className={usage.data.contentRunwayDays < 30 ? 'text-oxblood' : 'text-soft'}
        >
          {usage.data.contentRunwayDays} days
        </span>
      </Row>
    </Section>
  );
}

// ---------------------------------------------------------------- backups

function BackupsSection() {
  const qc = useQueryClient();
  const status = useQuery({
    queryKey: ['backup-status'],
    queryFn: () =>
      api.get<{
        runs: BackupRun[];
        lastD1: BackupRun | null;
        lastMirror: BackupRun | null;
      }>('/backup/status'),
    retry: false,
    refetchOnWindowFocus: true,
  });
  const snapshot = useMutation({
    mutationFn: () => api.post<{ ok: true; bytes: number }>('/backup/snapshot'),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['backup-status'] }),
  });
  if (status.error instanceof ApiError && status.error.status === 403) return null;
  if (!status.data) return null;

  return (
    <Section title="Backups">
      <p className="text-soft text-sm mb-3">
        Nightly D1 dump + photo mirror to a second R2 bucket. Last 30 days are kept.
        Manual snapshot before risky cleanup.
      </p>
      <Row label="Last D1 dump">
        <span className="text-soft text-sm">
          {status.data.lastD1
            ? new Date(status.data.lastD1.startedAt).toLocaleString()
            : 'never'}
        </span>
      </Row>
      <Row label="Last photo mirror">
        <span className="text-soft text-sm">
          {status.data.lastMirror
            ? new Date(status.data.lastMirror.startedAt).toLocaleString()
            : 'never'}
        </span>
      </Row>
      <div className="mt-3">
        <Button
          variant="secondary"
          size="sm"
          onClick={() => snapshot.mutate()}
          busy={snapshot.isPending}
        >
          Snapshot now
        </Button>
        {snapshot.isSuccess && (
          <span className="ml-3 text-sage text-sm">
            Done — {(snapshot.data.bytes / 1024).toFixed(1)} KB
          </span>
        )}
      </div>
    </Section>
  );
}

// --------------------------------------------------------------- playlists

function PlaylistFeedsSection() {
  const qc = useQueryClient();
  const feeds = useQuery({
    queryKey: ['playlist-feeds'],
    queryFn: () => api.get<{ feeds: PlaylistFeed[] }>('/playlist-feeds'),
    retry: false,
  });
  const create = useMutation({
    mutationFn: (body: { url: string; name?: string }) =>
      api.post('/playlist-feeds', body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['playlist-feeds'] }),
  });
  const update = useMutation({
    mutationFn: ({
      id,
      ...body
    }: {
      id: string;
      enabled?: boolean;
      name?: string;
      defaultTags?: VideoTag[];
    }) => api.patch(`/playlist-feeds/${id}`, body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['playlist-feeds'] }),
  });
  const del = useMutation({
    mutationFn: (id: string) => api.delete(`/playlist-feeds/${id}`),
    onSuccess: () => {
      setPendingDelete(null);
      void qc.invalidateQueries({ queryKey: ['playlist-feeds'] });
    },
  });
  const sync = useMutation({
    mutationFn: () =>
      api.post<{ feeds: number; inserted: number; errors: string[] }>(
        '/playlist-feeds/sync',
      ),
  });

  const [url, setUrl] = useState('');
  const [name, setName] = useState('');
  const [addErr, setAddErr] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<PlaylistFeed | null>(null);

  if (feeds.error instanceof ApiError && feeds.error.status === 403) return null;
  if (!feeds.data) return null;

  return (
    <Section title="Playlists">
      <p className="text-soft text-sm mb-3">
        YouTube playlists that feed the daily devotional video. The weekly sync pulls
        every enabled playlist; toggle off to pause without deleting.
      </p>

      <div className="space-y-2 mb-4">
        {feeds.data.feeds.length === 0 && (
          <p className="text-soft text-sm italic">No playlists configured.</p>
        )}
        {feeds.data.feeds.map((f) => {
          let parsedDefaults: VideoTag[] = [];
          try {
            const j = JSON.parse(f.defaultTags || '[]') as unknown;
            if (Array.isArray(j)) {
              parsedDefaults = j.filter((x): x is VideoTag =>
                (VIDEO_TAGS as readonly string[]).includes(x as string),
              );
            }
          } catch {
            /* malformed → treat as empty */
          }
          return (
            <div
              key={f.id}
              className="py-3 border-b border-line last:border-b-0 space-y-2"
            >
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={f.enabled}
                  onChange={(e) => update.mutate({ id: f.id, enabled: e.target.checked })}
                  className="accent-oxblood w-5 h-5 shrink-0"
                  aria-label={`${f.enabled ? 'Disable' : 'Enable'} ${f.name}`}
                />
                <div className="flex-1 min-w-0">
                  <div className="text-ink truncate">{f.name}</div>
                  <a
                    href={f.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-soft text-xs underline truncate block"
                  >
                    {f.playlistId}
                  </a>
                </div>
                <button
                  onClick={() => setPendingDelete(f)}
                  className="text-soft text-sm underline shrink-0"
                  aria-label={`Delete ${f.name}`}
                >
                  Delete
                </button>
              </div>
              <div>
                <div className="text-xs text-soft mb-1">Default tags for new videos</div>
                <TagChips
                  value={parsedDefaults}
                  onChange={(next) => update.mutate({ id: f.id, defaultTags: next })}
                />
              </div>
            </div>
          );
        })}
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          setAddErr(null);
          if (!url.trim()) return;
          create.mutate(
            { url: url.trim(), name: name.trim() || undefined },
            {
              onSuccess: () => {
                setUrl('');
                setName('');
              },
              onError: (err: Error) => setAddErr(err.message),
            },
          );
        }}
        className="space-y-2"
      >
        <label className="block">
          <span className="text-soft text-xs">YouTube playlist URL or PL… id</span>
          <input
            type="text"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://www.youtube.com/playlist?list=PL…"
            className="block w-full mt-1 px-3 py-2 border border-line rounded-lg bg-paper text-ink"
          />
        </label>
        <label className="block">
          <span className="text-soft text-xs">Display name (optional)</span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Our Daily Bread Devotionals"
            className="block w-full mt-1 px-3 py-2 border border-line rounded-lg bg-paper text-ink"
          />
        </label>
        {addErr && <p className="text-oxblood text-sm">{addErr}</p>}
        <button
          type="submit"
          disabled={create.isPending || !url.trim()}
          className="h-10 px-4 rounded-lg bg-oxblood text-paper text-sm font-medium disabled:opacity-50"
        >
          {create.isPending ? 'Adding…' : 'Add playlist'}
        </button>
      </form>

      <div className="mt-4 flex items-center gap-3">
        <Button
          variant="secondary"
          size="sm"
          onClick={() => sync.mutate()}
          busy={sync.isPending}
        >
          Sync all feeds now
        </Button>
        {sync.isSuccess && (
          <span className="text-sage text-sm">
            {sync.data.inserted} videos from {sync.data.feeds} feed
            {sync.data.feeds === 1 ? '' : 's'}
            {sync.data.errors.length > 0 &&
              ` (${sync.data.errors.length} error${sync.data.errors.length === 1 ? '' : 's'})`}
          </span>
        )}
        {sync.error instanceof Error && (
          <span className="text-oxblood text-sm">{sync.error.message}</span>
        )}
      </div>

      {pendingDelete && (
        <ConfirmDialog
          title={`Delete "${pendingDelete.name}"?`}
          message="The playlist stops feeding the daily video. Videos already imported are kept."
          confirmLabel="Delete"
          destructive
          busy={del.isPending}
          error={del.isError ? 'Could not delete. Try again.' : null}
          onConfirm={() => del.mutate(pendingDelete.id)}
          onCancel={() => setPendingDelete(null)}
        />
      )}
    </Section>
  );
}

// ------------------------------------------------------------------ videos

function VideosCurationSection() {
  const qc = useQueryClient();
  const videos = useQuery({
    queryKey: ['videos'],
    queryFn: () => api.get<{ videos: VideoRow[] }>('/videos'),
    retry: false,
  });
  const setTags = useMutation({
    mutationFn: ({ videoId, tags }: { videoId: string; tags: VideoTag[] }) =>
      api.patch(`/videos/${videoId}`, { tags }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['videos'] }),
  });
  const autoTag = useMutation({
    mutationFn: () =>
      api.post<{
        scanned: number;
        tagged: number;
        remaining: number;
        errors: string[];
      }>('/videos/auto-tag', undefined, { timeoutMs: 90_000 }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['videos'] }),
  });

  if (videos.error instanceof ApiError && videos.error.status === 403) return null;
  if (!videos.data) return null;

  return (
    <Section title="Videos">
      <p className="text-soft text-sm mb-3">
        Tag each video so it appears under the matching theme in My Walk → Go Deeper. New
        videos inherit their playlist's default tags; tweak any one here.
      </p>
      <div className="flex items-center gap-3 mb-4">
        <button
          type="button"
          onClick={() => autoTag.mutate()}
          disabled={autoTag.isPending}
          className="px-3 h-8 rounded-full text-xs border border-line bg-paper text-ink disabled:opacity-50"
        >
          {autoTag.isPending ? 'Auto-tagging…' : 'Auto-tag untagged'}
        </button>
        {autoTag.isSuccess && (
          <span className="text-xs text-soft">
            Tagged {autoTag.data.tagged} of {autoTag.data.scanned} this run.
            {autoTag.data.remaining > 0
              ? ` ${autoTag.data.remaining} still untagged — click again to continue.`
              : ' All caught up.'}
          </span>
        )}
        {autoTag.isError && (
          <span className="text-xs text-oxblood">
            {autoTag.error instanceof Error
              ? autoTag.error.message
              : 'Auto-tag failed — try again.'}
          </span>
        )}
      </div>
      {videos.data.videos.length === 0 ? (
        <p className="text-soft text-sm italic">
          No videos yet. Add a playlist above and sync to populate the catalog.
        </p>
      ) : (
        <div className="space-y-3">
          {videos.data.videos.map((v) => {
            const current = v.tags.filter((t): t is VideoTag =>
              (VIDEO_TAGS as readonly string[]).includes(t),
            );
            return (
              <div key={v.videoId} className="py-2 border-b border-line last:border-b-0">
                <div className="flex items-start gap-3">
                  <img
                    src={`https://i.ytimg.com/vi/${encodeURIComponent(v.videoId)}/default.jpg`}
                    alt=""
                    width={80}
                    height={60}
                    loading="lazy"
                    decoding="async"
                    className="w-20 aspect-video object-cover rounded bg-line shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-ink text-sm leading-snug line-clamp-2">
                      {v.title}
                    </div>
                    {v.uploadDate && (
                      <div className="text-xs text-muted mt-1">{v.uploadDate}</div>
                    )}
                  </div>
                </div>
                <div className="mt-2">
                  <TagChips
                    value={current}
                    onChange={(next) =>
                      setTags.mutate({ videoId: v.videoId, tags: next })
                    }
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Section>
  );
}

// ------------------------------------------------------------------ helpers

/** Multi-select tag chips used by both the Playlists default-tags
 *  editor and the Videos per-row editor. Saves on every toggle. */
function TagChips({
  value,
  onChange,
}: {
  value: VideoTag[];
  onChange: (next: VideoTag[]) => void;
}) {
  const set = new Set(value);
  return (
    <div className="flex flex-wrap gap-1.5">
      {VIDEO_TAGS.map((t) => {
        const on = set.has(t);
        return (
          <button
            key={t}
            type="button"
            onClick={() => {
              const next = on ? value.filter((x) => x !== t) : [...value, t];
              onChange(next);
            }}
            className={`px-2.5 h-7 rounded-full text-xs border ${
              on
                ? 'bg-oxblood text-paper border-oxblood'
                : 'text-soft border-line bg-paper'
            }`}
          >
            {TAG_LABEL[t]}
          </button>
        );
      })}
    </div>
  );
}

// ----------------------------------------------------- email diagnostic
// Owner-only one-shot Resend probe. Calls /admin/email-test with the
// current RESEND_FROM and surfaces Resend's verbatim response so the
// "did the email send?" loop closes in one tap. Bypasses the
// existence-check + OTP-row dance that auth-email / me-email do, so
// failures here are unambiguously about the Worker→Resend leg.
interface EmailTestResult {
  ok: boolean;
  status: number;
  response: unknown;
  sentFrom: string;
  sentTo: string;
  hint?: string;
}

function EmailDiagnosticSection() {
  const [to, setTo] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<EmailTestResult | null>(null);
  const [err, setErr] = useState<string | null>(null);

  async function send() {
    setBusy(true);
    setErr(null);
    setResult(null);
    try {
      const res = await api.post<EmailTestResult>('/admin/email-test', { to });
      setResult(res);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Section title="Email diagnostic">
      <p className="text-soft text-sm mb-3">
        Sends one test email via Resend using the Worker's current RESEND_FROM +
        RESEND_API_KEY secrets and shows the raw response. If the production sign-in email
        flow is failing, this is the cleanest place to see why.
      </p>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (to.trim()) void send();
        }}
        className="flex gap-2"
      >
        <input
          type="email"
          inputMode="email"
          value={to}
          onChange={(e) => setTo(e.target.value)}
          placeholder="recipient@example.com"
          className="flex-1 h-10 px-3 rounded-lg border border-line bg-paper text-ink"
        />
        <button
          type="submit"
          disabled={busy || !to.trim()}
          className="h-10 px-4 rounded-lg bg-oxblood text-paper text-sm font-medium disabled:opacity-50"
        >
          {busy ? 'Sending…' : 'Send test'}
        </button>
      </form>
      {err && <p className="mt-2 text-oxblood text-sm">{err}</p>}
      {result && (
        <div className="mt-3 space-y-2 text-sm">
          <div
            className={`px-3 py-2 rounded-lg ${
              result.ok ? 'bg-sage/10 text-sage' : 'bg-oxblood/10 text-oxblood'
            }`}
          >
            <div className="font-medium">
              {result.ok
                ? `Sent (HTTP ${result.status})`
                : `Rejected (HTTP ${result.status})`}
            </div>
            <div className="text-xs mt-1 text-soft">
              from <span className="font-mono">{result.sentFrom}</span> · to{' '}
              <span className="font-mono">{result.sentTo}</span>
            </div>
            {result.hint && (
              <div className="mt-2 text-xs leading-snug">{result.hint}</div>
            )}
          </div>
          <details className="text-soft">
            <summary className="cursor-pointer text-xs">Resend response (raw)</summary>
            <pre className="mt-2 p-3 bg-card border border-line rounded-lg overflow-x-auto text-xs whitespace-pre-wrap break-words">
              {JSON.stringify(result.response, null, 2)}
            </pre>
          </details>
        </div>
      )}
    </Section>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between py-2">
      <span className="text-soft text-sm">{label}</span>
      {children}
    </div>
  );
}

function Meter({ label, pct }: { label: string; pct: number }) {
  const clamped = Math.min(Math.max(pct, 0), 100);
  const bar = pct >= 80 ? 'bg-oxblood' : pct >= 60 ? 'bg-yellow-600' : 'bg-sage';
  return (
    <div className="py-2">
      <div className="flex items-center justify-between mb-1">
        <span className="text-soft text-sm">{label}</span>
        <span className="text-soft text-sm font-mono">{pct}%</span>
      </div>
      <div className="h-2 rounded-full bg-line overflow-hidden">
        <div
          className={`h-full ${bar} transition-all`}
          style={{ width: `${clamped}%` }}
        />
      </div>
    </div>
  );
}
